import { useEffect, useState } from "react";
import { Pin, Trash2, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar, UserLink } from "@/components/UserBits";
import { Switch } from "@/components/ui/switch";

const ago = (at) => new Date(at).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });

export function ProfileWall({ p }) {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/users/${p.user_id}/wall`).then(({ data }) => setD(data)).catch(() => setD(null));
  useEffect(() => { load(); }, [p.user_id]); // eslint-disable-line react-hooks/exhaustive-deps
  const send = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/users/${p.user_id}/wall`, { text }); setD((x) => ({ ...x, posts: [data, ...x.posts] })); setText(""); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  const del = async (id) => { try { await api.delete(`/wall/${id}`); setD((x) => ({ ...x, posts: x.posts.filter((q) => q.id !== id) })); } catch (e) { toast.error(formatErr(e)); } };
  const toggle = async (enabled) => { try { await api.put("/me/wall", { enabled }); await load(); } catch (e) { toast.error(formatErr(e)); } };
  if (!d) return null;
  const staff = ["admin", "moderator"].includes(user?.role);
  return (
    <div className="bg-white rounded-3xl border border-pink-100 p-5 space-y-3" data-testid="profile-wall">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-bold text-lg flex items-center gap-2"><Pin className="w-4 h-4 text-rose-500" /> Pinnwand</h3>
      </div>
      {p.is_me && <label className="flex items-center justify-between text-xs text-slate-500">Andere dürfen mir schreiben <Switch checked={d.enabled} onCheckedChange={toggle} data-testid="wall-toggle" /></label>}
      {!d.enabled && !p.is_me && <p className="text-sm text-slate-400" data-testid="wall-disabled">Die Pinnwand ist ausgeschaltet.</p>}
      {d.can_write && !p.is_me && (
        <div className="flex gap-2">
          <input value={text} onChange={(e) => setText(e.target.value)} maxLength={280} placeholder={`Schreib ${p.name} etwas Nettes…`} data-testid="wall-input" onKeyDown={(e) => e.key === "Enter" && text.trim() && send()}
            className="flex-1 min-w-0 h-11 px-4 rounded-full border border-pink-100 text-sm focus:outline-none focus:ring-2 focus:ring-rose-200" />
          <button onClick={send} disabled={!text.trim() || busy} data-testid="wall-send" aria-label="Senden" className="w-11 h-11 shrink-0 rounded-full pink-gradient text-white flex items-center justify-center disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</button>
        </div>
      )}
      {(d.enabled || d.posts.length > 0) && (d.posts.length === 0 ? <p className="text-sm text-slate-400">Noch keine Nachrichten.</p> : (
        <ul className="space-y-2 max-h-96 overflow-y-auto">
          {d.posts.map((x) => (
            <li key={x.id} className="flex gap-2.5 p-3 rounded-2xl bg-rose-50/50" data-testid={`wall-post-${x.id}`}>
              <UserAvatar userId={x.author_id} name={x.author?.name} picture={x.author?.picture} size="w-8 h-8" text="text-xs" />
              <div className="flex-1 min-w-0">
                <p className="text-xs"><UserLink id={x.author_id} className="font-bold">{x.author?.name || "Mitglied"}</UserLink> <span className="text-slate-400">· {ago(x.created_at)}</span></p>
                <p className="text-sm break-words">{x.text}</p>
              </div>
              {(p.is_me || x.author_id === user?.user_id || staff) && <button onClick={() => del(x.id)} data-testid={`wall-delete-${x.id}`} aria-label="Löschen" className="p-1.5 h-fit rounded-full text-slate-400 hover:text-red-600 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5" /></button>}
            </li>
          ))}
        </ul>
      ))}
    </div>
  );
}
